import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@mui/material';
import { Add } from '@mui/icons-material';
import _ from 'lodash';
import { SiteTableColumn, SiteId, SiteDefaultSort, SiteTableColumnTitles, Site } from 'constants/sites.constants';
import { userStatuses } from 'constants/user.constants';
import { ColumnAlign, IColumnType } from 'components/Table/types';
import SelectBox from 'components/SelectBox';
import BasicTable from 'components/Table';
import { pagesEnum } from 'constants/ui.constants';
import { useAppSelector } from 'hooks/redux.hooks';
import CircularIndeterminate from 'components/Loading';
import NoInfo from 'components/NoInfo';
import { filterByFields } from '../../utils/global.util';
import { DataFetchKeys, useDataFetchContext } from '../../context/DataFetchContext';
import { PageContentAddNew, PageContentContainer, PageContentFilter, PageContentTable } from '../PageContentContainer';
import { useDebounce } from 'hooks/useDebounce';

const MAX_LENGTH = { name: 40 };

const SitesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loadDataByKeys } = useDataFetchContext();
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedValue = useDebounce(searchValue);
  const { sites, loading, error } = useAppSelector((state) => state.sites);
  const { users } = useAppSelector((state) => state.user);

  const parsedSitesOptions = useMemo(() => _.uniq(_.map(sites, (site) => site.name)), [sites]);
  const parsedCountriesOptions = useMemo(() => _.uniq(_.map(sites, (site) => site.country)), [sites]);

  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>(() => ({
    country: {
      displayName: 'Country',
      options: [],
      value: ['All'],
      multiple: true
    },
    sites: {
      displayName: 'Sites',
      options: [],
      value: ['All'],
      multiple: true
    },
    status: {
      displayName: 'Status',
      options: userStatuses,
      value: 'All'
    }
  }));

  const sitesColumnAlign: { [key: string]: ColumnAlign } = {
    number: 'center',
    string: 'left'
  };

  const sitesColumns = useMemo(() => {
    const columns: IColumnType[] = [];
    _.keys(SiteTableColumn).forEach((element) => {
      const props: IColumnType = {
        fieldId: SiteId,
        name: element,
        title: SiteTableColumnTitles[element as keyof typeof SiteTableColumnTitles] || element,
        type: SiteTableColumn[element],
        align: sitesColumnAlign[SiteTableColumn[element] as keyof typeof sitesColumnAlign],
        customValueHandle: (val, rec) => {
          if (_.isArray(val)) {
            return val.length;
          }
          return rec.active ? 'Active' : 'Inactive';
        }
      };
      columns.push(props);
    });
    return columns;
  }, []);

  const updateURLParams = useCallback(
    (updatedFilterValues: { [key: string]: any }) => {
      const params = new URLSearchParams();
      _.forEach(updatedFilterValues, (value, key) => {
        if (key === 'status') {
          if (value.value && value.value !== 'All') {
            params.append(key, value.value);
          }
        } else if (value.value?.length && !value.value.includes('All')) {
          params.append(key, value.value.join(','));
        }
      });
      navigate(`?${params.toString()}`, { replace: true });
    },
    [navigate]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialFilterValues: { [key: string]: any } = {};
    params.forEach((value, key) => {
      initialFilterValues[key] = {
        ...initialFilterValues[key],
        value: key === 'status' ? value : value.split(',')
      };
    });

    setFilterValues((prevFilterValues) => ({
      country: {
        ...prevFilterValues.country,
        options: parsedCountriesOptions,
        value: initialFilterValues.country?.value || ['All']
      },
      sites: {
        ...prevFilterValues.sites,
        options: parsedSitesOptions,
        value: initialFilterValues.sites?.value || ['All']
      },
      status: {
        ...prevFilterValues.status,
        value: initialFilterValues.status?.value || 'All'
      }
    }));
  }, [location.search, parsedSitesOptions, parsedCountriesOptions]);

  useEffect(() => {
    if (!sites?.length) {
      loadDataByKeys([DataFetchKeys.SITES]);
    }
    if (!users?.length) {
      loadDataByKeys([DataFetchKeys.USERS]);
    }
  }, [sites?.length, users?.length]);

  const updateTableData = useCallback(() => {
    if (!sites || !filterValues?.country) return [];

    return sites.filter((site: Site) => {
      const isStatusMatch =
        !filterValues.status.value ||
        filterValues.status.value === 'All' ||
        (filterValues.status.value === 'Active') === site.active;

      const isCountryMatch =
        filterValues.country.value.includes('All') || filterValues.country.value.includes(site.country);

      const isSiteMatch = filterValues.sites.value.includes('All') || filterValues.sites.value.includes(site.name);

      return isStatusMatch && isCountryMatch && isSiteMatch;
    });
  }, [sites, filterValues]);

  const filteredSitesData = useMemo(() => {
    const filtered = updateTableData();
    return filterByFields(filtered, debouncedValue, ['country', 'assignedBWMR', 'name']);
  }, [updateTableData, debouncedValue]);

  const onFilterChange = (filterType: string, filterValue: string | string[]) => {
    const updatedFilterValues = {
      ...filterValues,
      [filterType]: { ...filterValues![filterType as keyof typeof filterValues], value: filterValue }
    };
    setFilterValues(updatedFilterValues);
    updateURLParams(updatedFilterValues);
  };

  const onResetSelections = () => {
    const resetedFilterValues = { ...filterValues };
    _.chain(filterValues)
      .keys()
      .forEach((filterValue) => {
        if (['country', 'sites'].includes(filterValue)) {
          resetedFilterValues[filterValue].value = ['All'];
        } else {
          resetedFilterValues[filterValue].value = 'All';
        }
      })
      .value();
    setFilterValues(resetedFilterValues);
    updateURLParams(resetedFilterValues);
    setSearchValue('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const actionCell = useMemo(() => {
    return [
      {
        element: (
          <Button
            color='secondary'
            sx={{ marginRight: '46px', textTransform: 'none', fontWeight: 'bold' }}
            variant='text'
          >
            Info
          </Button>
        ),
        onClick: (site) => {
          navigate(`/${pagesEnum.SITES}/${site.id}`, { state: { site } });
        }
      }
    ];
  }, [navigate]);

  if (error) return <NoInfo />;
  if (loading) return <CircularIndeterminate />;

  return (
    <PageContentContainer
      pageName='Sites'
      searchValue={searchValue}
      filteredResultsCount={filteredSitesData?.length || 0}
      resetFilterClicked={onResetSelections}
      searchValueChanged={handleInputChange}
    >
      <PageContentAddNew>
        <Button
          sx={{ textTransform: 'none' }}
          color='secondary'
          variant='contained'
          startIcon={<Add />}
          onClick={() => navigate(`/${pagesEnum.SITES}/new`)}
        >
          Add New Site
        </Button>
      </PageContentAddNew>
      <PageContentFilter>
        {_.map(_.keys(filterValues), (filterValuesKey) => (
          <SelectBox
            key={filterValuesKey}
            items={filterValues?.[filterValuesKey].options}
            values={filterValues?.[filterValuesKey].value}
            label={filterValues?.[filterValuesKey].displayName}
            inputLabel={filterValues?.[filterValuesKey].displayName}
            type={filterValuesKey}
            onFilterChange={onFilterChange}
            withAllOption
            multiple={filterValues?.[filterValuesKey].multiple}
          />
        ))}
      </PageContentFilter>
      <PageContentTable>
        <BasicTable
          fieldId='id'
          data={filteredSitesData || []}
          columns={sitesColumns}
          defaultSortBy={SiteDefaultSort}
          actionsCell={actionCell}
          maxLength={MAX_LENGTH}
        />
      </PageContentTable>
    </PageContentContainer>
  );
};

export default SitesPage;
